/**
 * Permissions API Client (Legacy Plugin Registry)
 *
 * @deprecated These functions target the older `/api/platform/permissions/*` endpoints.
 * Prefer the unified RBAC API from `rbac-api.ts` which uses `/api/platform/rbac/*`
 * and supports the full DB-backed authorization model.
 *
 * Migration guide:
 *   getPermissions()          → getRbacPermissions()
 *   getPermissionsByCategory()→ getRbacPermissionsByCategory()
 *   grantPermission()         → grantRbacPermission()
 *   revokePermission()        → removeRbacPermissionOverride()
 *   assignRole()              → assignRbacRole()
 *   removeRole()              → removeRbacRole()
 *   getMyPermissions()        → getUserRbacPermissions(userId)
 *   checkPermissions()        → (no legacy equivalent — use rbac-api directly)
 */

import client from './client';

const api = {
  get: <T = unknown>(path: string, config?: Record<string, unknown>) => client.get<T>(`/api/platform${path}`, config),
  post: <T = unknown>(path: string, data?: unknown, config?: Record<string, unknown>) => client.post<T>(`/api/platform${path}`, data, config),
  put: <T = unknown>(path: string, data?: unknown, config?: Record<string, unknown>) => client.put<T>(`/api/platform${path}`, data, config),
  delete: <T = unknown>(path: string, config?: Record<string, unknown>) => client.delete<T>(`/api/platform${path}`, config),
};

// ============================================================
// PERMISSIONS INTERFACES
// ============================================================

export interface Permission {
  id: string;
  pluginId: string;
  description: string;
  category?: string;
  scope?: 'global' | 'user' | 'plugin' | 'resource';
  isSystem?: boolean;
  registeredAt: string;
}

export interface Role {
  id: string;
  pluginId: string;
  name: string;
  description?: string;
  permissions: string[];
  isSystem?: boolean;
}

export interface PermissionsData {
  permissions: Permission[];
  roles: Role[];
}

export interface MyPermissions {
  permissions: string[];
  roles: Role[];
}

export interface PermissionCheckResult {
  granted: boolean;
  results: Record<string, boolean>;
  missingPermissions: string[];
  reason?: string;
}

// ============================================================
// PERMISSIONS FUNCTIONS
// ============================================================

/** @deprecated Use `getRbacPermissions()` from `rbac-api.ts` */
export async function getPermissions(): Promise<PermissionsData> {
  const response = await api.get<PermissionsData>('/permissions');
  return response.data;
}

export async function getMyPermissions(): Promise<MyPermissions> {
  const response = await api.get<MyPermissions>('/permissions/my');
  return response.data;
}

export async function checkPermissions(permissions: string | string[]): Promise<PermissionCheckResult> {
  const response = await api.post<PermissionCheckResult>('/permissions/check', { permissions });
  return response.data;
}

/** @deprecated Use `getRbacPermissionsByCategory()` from `rbac-api.ts` */
export async function getPermissionsByCategory(): Promise<Record<string, Permission[]>> {
  const response = await api.get<Record<string, Permission[]>>('/permissions/by-category');
  return response.data;
}

/** @deprecated Use `grantRbacPermission()` from `rbac-api.ts` */
export async function grantPermission(userId: string, permissionId: string): Promise<{ success: boolean }> {
  const response = await api.post<{ success: boolean }>('/permissions/grant', { userId, permissionId });
  return response.data;
}

/** @deprecated Use `removeRbacPermissionOverride()` from `rbac-api.ts` */
export async function revokePermission(userId: string, permissionId: string): Promise<{ success: boolean }> {
  const response = await api.post<{ success: boolean }>('/permissions/revoke', { userId, permissionId });
  return response.data;
}

/** @deprecated Use `assignRbacRole()` from `rbac-api.ts` */
export async function assignRole(userId: string, roleId: string): Promise<{ success: boolean }> {
  const response = await api.post<{ success: boolean }>('/permissions/assign-role', { userId, roleId });
  return response.data;
}

/** @deprecated Use `removeRbacRole()` from `rbac-api.ts` */
export async function removeRole(userId: string, roleId: string): Promise<{ success: boolean }> {
  const response = await api.post<{ success: boolean }>('/permissions/remove-role', { userId, roleId });
  return response.data;
}
